import { useMemo } from 'react';

import type { IconTextBadgeItem } from '@/shared/ui/IconTextBadgeList';

import type { AssetSearchResultItem } from '@/entities/asset-search/domain/types';

type AssetSearchMetaItem = {
  type: 'dataset' | 'feature_set' | 'parameter_set' | 'artifact' | 'metrics';
  items: IconTextBadgeItem[];
};

export type UseAssetSearchMetaItemsOptions = {
  /** 데이터 탐색 등에서는 false(기본값)로 파라미터셋 배지를 숨깁니다 */
  includeParameterSetMeta?: boolean;
};

export const useAssetSearchMetaItems = (
  rawAssetItem: AssetSearchResultItem | null | undefined,
  options: UseAssetSearchMetaItemsOptions = {}
): AssetSearchMetaItem[] => {
  const { includeParameterSetMeta = false } = options;

  return useMemo(() => {
    if (!rawAssetItem) return [];

    const allItems: Record<string, AssetSearchMetaItem> = {};

    if (rawAssetItem.dataset) {
      const columns = rawAssetItem.dataset.columns || [];
      const topColumn = rawAssetItem.dataset.topMatchedValue || columns[0];
      if (topColumn) {
        allItems['dataset'] = {
          type: 'dataset',
          items: [
            {
              id: 'dataset',
              icon: 'database' as const,
              text: `Dataset: ${topColumn}`,
              badge:
                columns.length > 1
                  ? {
                      text: `+${columns.length - 1}`,
                    }
                  : undefined,
            },
          ],
        };
      }
    }

    if (rawAssetItem.featureSet) {
      const columns = rawAssetItem.featureSet.columns || [];
      const topColumn = rawAssetItem.featureSet.topMatchedValue || columns[0];
      if (topColumn) {
        allItems['feature_set'] = {
          type: 'feature_set',
          items: [
            {
              id: 'feature_set',
              icon: 'layers' as const,
              text: `Feature Set: ${topColumn}`,
              badge:
                columns.length > 1
                  ? {
                      text: `+${columns.length - 1}`,
                    }
                  : undefined,
            },
          ],
        };
      }
    }

    if (includeParameterSetMeta && rawAssetItem.parameterSet) {
      const paramKeys = Object.keys(rawAssetItem.parameterSet.hyperparameters);
      const topKey = rawAssetItem.parameterSet.topMatchedValue || paramKeys[0];
      if (topKey) {
        allItems['parameter_set'] = {
          type: 'parameter_set',
          items: [
            {
              id: 'parameter_set',
              icon: 'setting' as const,
              text: `Parameter Set: ${topKey}`,
              badge:
                paramKeys.length > 1
                  ? {
                      text: `+${paramKeys.length - 1}`,
                    }
                  : undefined,
            },
          ],
        };
      }
    }

    if (rawAssetItem.metrics && Object.keys(rawAssetItem.metrics).length > 0) {
      const metricsEntries = Object.entries(rawAssetItem.metrics);
      const firstMetric = metricsEntries[0];
      allItems['metrics'] = {
        type: 'metrics',
        items: [
          {
            id: 'metrics',
            icon: 'dashboard' as const,
            text: `Metrics: ${firstMetric[0]}`,
            badge:
              metricsEntries.length > 1
                ? {
                    text: `+${metricsEntries.length - 1}`,
                  }
                : undefined,
          },
        ],
      };
    }

    if (rawAssetItem.artifacts && rawAssetItem.artifacts.count > 0) {
      const files = rawAssetItem.artifacts.files ?? [];
      const artifactCount = rawAssetItem.artifacts.count;
      const firstFileEntry = files.find((f) => f.path && !f.isDir);
      const primaryPath =
        firstFileEntry?.path ?? files.find((f) => f.path)?.path ?? rawAssetItem.artifacts.topMatchedValue?.trim();

      let fileName: string;
      let artifactBadge: IconTextBadgeItem['badge'];

      if (primaryPath) {
        const normalizedPath = primaryPath.replace(/\\/g, '/');
        fileName = normalizedPath.split('/').filter(Boolean).pop() || primaryPath;
        artifactBadge =
          artifactCount > 1
            ? {
                text: `+${artifactCount - 1}`,
              }
            : undefined;
      } else {
        fileName = artifactCount === 1 ? '1 file' : `${artifactCount} files`;
        artifactBadge = undefined;
      }

      allItems['artifact'] = {
        type: 'artifact',
        items: [
          {
            id: 'artifacts',
            icon: 'data-product',
            text: `Artifact: ${fileName}`,
            badge: artifactBadge,
          },
        ],
      };
    }

    const order = ['dataset', 'feature_set', 'parameter_set', 'metrics', 'artifact'];

    return order.filter((key) => allItems[key]).map((key) => allItems[key]);
  }, [rawAssetItem, includeParameterSetMeta]);
};
